import { useEffect, useLayoutEffect, useRef, useState, type JSX, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { isSeparator, nextEnabledIndex, type MenuEntry } from './menuModel';
import './components.css';

interface MenuListProps {
  items: MenuEntry[];
  x: number;
  y: number;
  /** Alternative x used when the menu would overflow to the right (submenus flip to the left). */
  flipX?: number;
  label?: string;
  onCloseAll(): void;
  /** Close just this level (ArrowLeft / Escape in a submenu). */
  onCloseSelf(): void;
  /** Horizontal arrow keys not consumed by this menu (menu bar navigation). */
  onNavigate?(direction: 1 | -1): void;
}

function MenuList({ items, x, y, flipX, label, onCloseAll, onCloseSelf, onNavigate }: MenuListProps): JSX.Element {
  const root = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(-1);
  const [sub, setSub] = useState<{ index: number; x: number; y: number; flipX: number } | null>(null);
  const [pos, setPos] = useState({ x, y });

  useLayoutEffect(() => {
    const node = root.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    let nx = x;
    let ny = y;
    if (nx + rect.width > window.innerWidth - 4) nx = flipX !== undefined ? flipX - rect.width : window.innerWidth - rect.width - 4;
    if (ny + rect.height > window.innerHeight - 4) ny = window.innerHeight - rect.height - 4;
    setPos({ x: Math.max(4, nx), y: Math.max(4, ny) });
    node.focus();
  }, [x, y, flipX]);

  const openSub = (index: number): void => {
    const row = root.current?.querySelector<HTMLElement>(`[data-index="${index}"]`);
    const entry = items[index];
    if (!row || !entry || isSeparator(entry) || !entry.submenu || entry.disabled) return;
    const rect = row.getBoundingClientRect();
    setSub({ index, x: rect.right - 2, y: rect.top - 4, flipX: rect.left + 2 });
  };

  const choose = (index: number): void => {
    const entry = items[index];
    if (!entry || isSeparator(entry) || entry.disabled) return;
    if (entry.submenu) {
      openSub(index);
      return;
    }
    onCloseAll();
    entry.onSelect?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (sub) return;
    let handled = true;
    switch (event.key) {
      case 'ArrowDown':
        setActive(nextEnabledIndex(items, active, 1));
        break;
      case 'ArrowUp':
        setActive(nextEnabledIndex(items, active < 0 ? 0 : active, -1));
        break;
      case 'Home':
        setActive(nextEnabledIndex(items, -1, 1));
        break;
      case 'End':
        setActive(nextEnabledIndex(items, 0, -1));
        break;
      case 'Enter':
      case ' ':
        choose(active);
        break;
      case 'ArrowRight': {
        const entry = items[active];
        if (entry && !isSeparator(entry) && entry.submenu) openSub(active);
        else if (onNavigate) onNavigate(1);
        else handled = false;
        break;
      }
      case 'ArrowLeft':
        if (onNavigate) onNavigate(-1);
        else onCloseSelf();
        break;
      case 'Escape':
        onCloseSelf();
        break;
      case 'Tab':
        onCloseAll();
        break;
      default:
        handled = false;
    }
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const subEntry = sub ? items[sub.index] : undefined;

  return (
    <>
      <div
        ref={root}
        className="ui-menu"
        role="menu"
        aria-label={label}
        tabIndex={-1}
        style={{ left: pos.x, top: pos.y }}
        onKeyDown={onKeyDown}
        onContextMenu={(e) => e.preventDefault()}
      >
        {items.map((entry, index) =>
          isSeparator(entry) ? (
            <div key={index} className="ui-menu-separator" role="separator" />
          ) : (
            <div
              key={index}
              data-index={index}
              role={entry.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
              aria-checked={entry.checked}
              aria-disabled={entry.disabled || undefined}
              aria-haspopup={entry.submenu ? 'menu' : undefined}
              aria-expanded={entry.submenu ? sub?.index === index : undefined}
              className={[
                'ui-menu-item',
                active === index || sub?.index === index ? 'active' : '',
                entry.disabled ? 'disabled' : '',
                entry.danger ? 'danger' : '',
              ].join(' ')}
              onPointerEnter={() => {
                setActive(index);
                if (entry.submenu && !entry.disabled) openSub(index);
                else setSub(null);
              }}
              onClick={() => choose(index)}
            >
              <span>{entry.checked ? <Icon name="check" size={14} /> : null}</span>
              <span>{entry.label}</span>
              <span className="ui-menu-shortcut">{entry.shortcut ?? ''}</span>
              <span>{entry.submenu ? <Icon name="chevronRight" size={12} /> : null}</span>
            </div>
          ),
        )}
      </div>
      {sub && subEntry && !isSeparator(subEntry) && subEntry.submenu ? (
        <MenuList
          items={subEntry.submenu}
          x={sub.x}
          y={sub.y}
          flipX={sub.flipX}
          label={subEntry.label}
          onCloseAll={onCloseAll}
          onCloseSelf={() => {
            setSub(null);
            root.current?.focus();
          }}
        />
      ) : null}
    </>
  );
}

export interface MenuPopupProps {
  items: MenuEntry[];
  x: number;
  y: number;
  label?: string;
  onClose(): void;
  onNavigate?(direction: 1 | -1): void;
}

/** A popup menu with keyboard navigation and nested submenus; closes on outside press. */
export function MenuPopup({ items, x, y, label, onClose, onNavigate }: MenuPopupProps): JSX.Element {
  const host = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      if (host.current && event.target instanceof Node && host.current.contains(event.target)) return;
      // A press on the control that owns this menu is that control's to handle (it toggles the menu).
      if (event.target instanceof Element && event.target.closest('[data-menu-anchor]')) return;
      onClose();
    };
    const onBlur = (): void => onClose();
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('blur', onBlur);
    window.addEventListener('resize', onBlur);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('resize', onBlur);
    };
  }, [onClose]);

  return createPortal(
    <div ref={host} className="studio-menu-host">
      <MenuList items={items} x={x} y={y} label={label} onCloseAll={onClose} onCloseSelf={onClose} onNavigate={onNavigate} />
    </div>,
    document.body,
  );
}
